import * as Sentry from "@sentry/react";

declare const __SENTRY_RELEASE__: string;

/**
 * Sentry DSN. A DSN is a publishable value, so it is safe in the codebase.
 * Set VITE_SENTRY_DSN to override per environment.
 */
export const SENTRY_DSN: string = import.meta.env.VITE_SENTRY_DSN ?? "";

export const sentryEnabled = Boolean(SENTRY_DSN);

/**
 * Must match the release name used when source maps were uploaded at build
 * time (see vite.config.ts), otherwise Sentry cannot un-minify stack traces.
 */
const SENTRY_RELEASE: string | undefined =
  (typeof __SENTRY_RELEASE__ !== "undefined" && __SENTRY_RELEASE__) ||
  import.meta.env.VITE_SENTRY_RELEASE ||
  undefined;

export function initSentry() {
  if (!sentryEnabled) {
    console.info(
      "[sentry] disabled — no DSN configured (set VITE_SENTRY_DSN to enable dashboard reporting).",
    );
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: SENTRY_RELEASE,
    // Full stack traces + component context for blank-screen crashes.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    attachStacktrace: true,
    sendDefaultPii: false,
  });

  attachBreadcrumbMirror();
}


/** Attach the signed-in user so dashboard events carry account context. */
export function setSentryUser(
  user: { id: string; email?: string | null } | null,
) {
  if (!sentryEnabled) return;
  Sentry.setUser(user ? { id: user.id, email: user.email ?? undefined } : null);
}

/* ------------------------------------------------------------------ */
/* Breadcrumbs                                                         */
/* Trail of key user actions attached to any error reported afterwards.*/
/* Keep payloads free of PII (no emails, names, or free-text answers). */
/* ------------------------------------------------------------------ */

type BreadcrumbData = Record<string, string | number | boolean | null | undefined>;

export type RecordedBreadcrumb = {
  id: number;
  timestamp: number;
  category: string;
  message: string;
  level: string;
  data?: Record<string, unknown>;
};

/* Local mirror of the breadcrumb trail so the admin debug page can show
 * exactly what context would be attached to the next Sentry event. */
const MAX_RECORDED = 100;
let recorded: RecordedBreadcrumb[] = [];
let nextId = 1;
let clientHookActive = false;
const listeners = new Set<(crumbs: RecordedBreadcrumb[]) => void>();

function record(crumb: Omit<RecordedBreadcrumb, "id" | "timestamp"> & { timestamp?: number }) {
  recorded = [
    ...recorded.slice(-(MAX_RECORDED - 1)),
    {
      id: nextId++,
      timestamp: crumb.timestamp ?? Date.now(),
      category: crumb.category || "default",
      message: crumb.message || "",
      level: crumb.level || "info",
      data: crumb.data,
    },
  ];
  listeners.forEach((fn) => fn(recorded));
}

/** Snapshot of the breadcrumbs collected so far (oldest first). */
export function getRecordedBreadcrumbs(): RecordedBreadcrumb[] {
  return recorded;
}

/** Clear the local mirror (does not affect Sentry's own buffer). */
export function clearRecordedBreadcrumbs() {
  recorded = [];
  listeners.forEach((fn) => fn(recorded));
}

/** Subscribe to breadcrumb updates. Returns an unsubscribe function. */
export function subscribeToBreadcrumbs(fn: (crumbs: RecordedBreadcrumb[]) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Attach to the Sentry client so automatic crumbs (navigation, fetch,
 * console, clicks) are mirrored too. Called from initSentry. */
function attachBreadcrumbMirror() {
  const client = Sentry.getClient();
  if (!client || typeof client.on !== "function") return;
  client.on("beforeAddBreadcrumb", (crumb) => {
    record({
      category: crumb.category ?? "default",
      message: crumb.message ?? crumb.type ?? "",
      level: (crumb.level as string) ?? "info",
      data: crumb.data,
      timestamp: crumb.timestamp ? crumb.timestamp * 1000 : undefined,
    });
  });
  clientHookActive = true;
}

function breadcrumb(
  category: string,
  message: string,
  data?: BreadcrumbData,
  level: "info" | "warning" | "error" = "info",
) {
  if (sentryEnabled) {
    Sentry.addBreadcrumb({ type: "user", category, message, level, data });
  }
  // When the client hook is live it already mirrored this crumb.
  if (!clientHookActive) record({ category, message, level, data });
}


/** Intake form lifecycle: step navigation, submit attempt, success/failure. */
export function trackIntake(
  message: string,
  data?: BreadcrumbData,
  level: "info" | "warning" | "error" = "info",
) {
  breadcrumb("intake", message, data, level);
}

/** File upload lifecycle across logo, module video, and submission uploads. */
export function trackFileUpload(
  message: string,
  data?: BreadcrumbData,
  level: "info" | "warning" | "error" = "info",
) {
  breadcrumb("file-upload", message, data, level);
}

/** Auth state transitions (sign in, sign out, token refresh, recovery). */
export function trackAuth(
  message: string,
  data?: BreadcrumbData,
  level: "info" | "warning" | "error" = "info",
) {
  breadcrumb("auth", message, data, level);
}

/** Debug/verification actions from the admin Sentry debug page. */
export function trackDebug(
  message: string,
  data?: BreadcrumbData,
  level: "info" | "warning" | "error" = "info",
) {
  breadcrumb("debug", message, data, level);
}

export { Sentry };
